/**
 * Shared date parsing utilities for AETHER.
 */

/**
 * Parses a flexible Zulu datetime string into a Date object (UTC).
 * Accepts space- or 'T'-separated values, with or without seconds, and with
 * or without a trailing 'Z' (e.g. "2026-05-07 12:35", "2026-05-07T12:35:00Z").
 * Returns null if the string is empty or unparseable.
 */
export function parseFlexibleDate(dt: string): Date | null {
  if (!dt || !dt.trim()) return null;
  let cleaned = dt.trim().replace(' ', 'T');
  // Normalize an existing trailing Z so we can reason about the time portion.
  const hasZ = cleaned.endsWith('Z');
  if (hasZ) cleaned = cleaned.slice(0, -1);
  const timePart = cleaned.split('T')[1] || '';
  // Add seconds when only HH:MM is present.
  if (timePart && timePart.split(':').length < 3) cleaned += ':00';
  cleaned += 'Z';
  const parsed = new Date(cleaned);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Adds a whole number of days to the date portion of a Zulu datetime string and
 * returns the result as a `YYYY-MM-DD` date string. Accepts space- or
 * 'T'-separated values (the time portion is ignored). Returns '' when the input
 * has no parseable date.
 */
export function addDaysToDateZulu(dt: string, days: number): string {
  if (!dt || !dt.trim()) return '';
  const datePart = dt.trim().split(/[ T]/)[0];
  const base = new Date(`${datePart}T00:00:00Z`);
  if (isNaN(base.getTime())) return '';
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString().split('T')[0];
}

/**
 * Returns the current date in Zulu (UTC) as a `YYYY-MM-DD` string. Used as the
 * default RTS-date filter so the Completed Events view opens on today's Zulu
 * date, consistent with the Zulu timestamps stored on events.
 */
export function todayDateZulu(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Returns the date portion (`YYYY-MM-DD`) of a Zulu datetime string, accepting
 * space- or 'T'-separated values. Returns '' when the input is empty. Used to
 * compare an event's ERTS/RTS date against the selected RTS-date filter.
 */
export function dateZuluOf(dt: string): string {
  if (!dt || !dt.trim()) return '';
  return dt.trim().split(/[ T]/)[0];
}
